import React from "react";

export default function PrivacyPolicy() {
  const sections = [
    {
      title: "1. Legal Information",
      content: [
        `We, CIPHERBIZZ FZCO, an entity having its registered office at A5, Dubai Digital Park, Dubai Silicon Oasis, Dubai, UAE, hereinafter referred to as the “Company”, “CIPHERBIZZ”, “we”, “our”, or “us”, are committed to protecting your privacy.`,
        `This Privacy Policy applies to our website www.cipherbizz.com, web platform, and mobile application, collectively referred to as the “Platform”.`,
      ],
    },
    {
      title: "2. Definitions",
      list: [
        `“User”, “you”, or “your” means any individual, employee, client, company representative, or authorized person using the Platform.`,
        `“Client” means an organization or employer using CIPHERBIZZ services to manage employee HR, payroll, attendance, leave, and related business processes.`,
        `“Personal Information” means information that can identify an individual directly or indirectly.`,
        `“Third Party” means any service provider, platform, company, or individual other than CIPHERBIZZ and the User.`,
      ],
    },
    {
      title: "3. Information We Collect",
      content: [
        `We may collect the following information based on your use of the Platform:`,
      ],
      list: [
        "Name",
        "Company details",
        "Employee ID or user ID",
        "Email address",
        "Phone number",
        "Login credentials and encrypted password",
        "Attendance information",
        "Leave requests and approval details",
        "Payroll and payslip-related information, where enabled by the employer",
        "Documents, images, or files uploaded for leave, claims, service requests, or HR-related purposes",
        "Device information such as device ID, browser details, IP address, operating system, and app version",
        "Platform usage details for analytics, support, and security",
        "Location information, only where enabled for attendance check-in/check-out or location-based attendance verification",
      ],
    },
    {
      title: "4. How We Use Your Information",
      content: [
        `We use your information for the following purposes:`,
      ],
      list: [
        "To provide HRMS, payroll, attendance, leave, employee self-service, and related services",
        "To create and maintain user accounts",
        "To verify attendance, check-in/check-out, and work location where applicable",
        "To process leave requests, approvals, service requests, and employee documents",
        "To provide payslips, payroll summaries, and HR-related information where enabled",
        "To send product updates, notifications, alerts, and support communication",
        "To improve the Platform, user experience, security, and service quality",
        "To maintain audit logs, internal records, and compliance requirements",
        "To prevent fraud, unauthorized access, misuse, or security incidents",
      ],
    },
    {
      title: "5. App Permissions",
      list: [
        "Camera / Photos / Files: Used for uploading documents, images, or attachments related to leave, claims, service requests, or employee profile updates.",
        "Location: Used only for attendance check-in/check-out or location-based attendance verification, where enabled by your employer.",
        "Notifications: Used to send HR alerts, approval updates, attendance reminders, leave updates, and service notifications.",
        "Internet / Network Access: Required to connect the app with CIPHERBIZZ services.",
        "We do not use these permissions for any purpose other than providing the intended HRMS services.",
      ],
    },
    {
      title: "6. Information Shared with Clients",
      content: [
        `If you are using the Platform through your employer or organization, your information may be accessible to authorized users of that organization, such as HR, payroll, managers, administrators, or approvers, based on role-based access permissions.`,
        `The employer or organization may determine how employee information is used, retained, corrected, or deleted within the Platform, subject to applicable laws and contractual obligations.`,
      ],
    },
    {
      title: "7. Sharing with Third Parties",
      content: [`We do not sell your personal information.`],
      list: [
        "Authorized employer or client administrators",
        "Cloud hosting and infrastructure service providers",
        "SMS, email, WhatsApp, or notification service providers, where enabled",
        "Analytics and monitoring service providers",
        "Technical support and implementation teams",
        "Legal, regulatory, or government authorities where required by law",
      ],
    },
    {
      title: "8. Cookies and Analytics",
      content: [
        `We may use cookies and similar technologies to improve the Platform experience.`,
      ],
      list: [
        "Authentication cookies",
        "Functionality cookies",
        "Tracking, optimization, and analytics cookies",
      ],
    },
    {
      title: "9. Data Security",
      content: [
        `We apply reasonable technical and organizational security measures to protect your information, including:`,
      ],
      list: [
        "HTTPS / TLS communication",
        "Role-based access control",
        "Authentication and authorization controls",
        "Secure password handling",
        "Server-side access restrictions",
        "Audit logs and activity monitoring",
        "Data backup and recovery controls",
      ],
    },
    {
      title: "10. Data Retention",
      content: [
        `We retain personal information only for as long as necessary to provide our services, comply with legal obligations, resolve disputes, enforce agreements, and support employer/client operational requirements.`,
      ],
    },
    {
      title: "11. Your Rights",
      list: [
        "Access your personal information",
        "Request correction of inaccurate information",
        "Request deletion of your information",
        "Restrict or object to certain processing",
        "Request data portability",
        "Withdraw consent where processing is based on consent",
      ],
    },
    {
      title: "12. Your Choices",
      list: [
        "Choose not to provide optional profile information",
        "Disable cookies in your browser settings",
        "Opt out of newsletters or non-essential communications",
        "Contact your employer or CIPHERBIZZ support for account or data-related requests",
      ],
    },
    {
      title: "13. Children’s Privacy",
      content: [
        `The Platform is intended for business, employer, and employee management purposes. It is not directed to children under the age of 13.`,
        `We do not knowingly collect personal information from children under 13.`,
      ],
    },
    {
      title: "14. External Links",
      content: [
        `Our Platform may contain links to third-party websites or services. We are not responsible for the privacy practices, content, or security of third-party websites.`,
      ],
    },
    {
      title: "15. Legal Disclosure",
      list: [
        "Comply with applicable laws or legal processes",
        "Protect our rights, users, clients, or systems",
        "Prevent fraud, misuse, or unauthorized access",
        "Enforce our terms, contracts, or policies",
      ],
    },
    {
      title: "16. Changes to This Privacy Policy",
      content: [
        `We may update this Privacy Policy from time to time. Updates may be posted on our website, mobile application, or communicated through email or platform notifications.`,
        `Your continued use of the Platform after updates means you accept the revised Privacy Policy.`,
      ],
    },
    {
      title: "17. Contact Us",
      list: [
        "Company: CIPHERBIZZ FZCO",
        "Registered Office: A5, Dubai Digital Park, Dubai Silicon Oasis, Dubai, UAE",
        "Website: www.cipherbizz.com",
        "Privacy Contact: support@cipherbizz.com",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-14 px-4 md:px-8">
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-3xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-700 to-purple-700 px-8 py-14 text-white">
          <p className="uppercase tracking-[4px] text-sm text-indigo-100 mb-3">
            Legal Information
          </p>
          <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
          <p className="mt-4 text-indigo-100 max-w-2xl leading-7">
            CIPHERBIZZ FZCO
          </p>
          <p className="text-indigo-200 mt-1">Last Updated: 26 April 2026</p>
        </div>

        <div className="px-6 md:px-10 py-10 space-y-12">
          {sections.map((section, index) => (
            <section
              key={index}
              className="border-b border-slate-100 pb-10 last:border-none"
            >
              <h2 className="text-2xl font-bold text-slate-800 mb-5">
                {section.title}
              </h2>

              {section.content &&
                section.content.map((para, i) => (
                  <p
                    key={i}
                    className="text-slate-600 leading-8 text-[16px] mb-4"
                  >
                    {para}
                  </p>
                ))}

              {section.list && (
                <ul className="space-y-3 mt-4">
                  {section.list.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-slate-600 leading-8"
                    >
                      <span className="mt-2 h-2 w-2 rounded-full bg-indigo-600"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="bg-slate-100 px-8 py-6 text-sm text-slate-500 text-center">
          © 2026 CIPHERBIZZ. All rights reserved.
        </div>
      </div>
    </div>
  );
}